/* ============================================================
   Bespoke Forms — shared client logic
   - Real-time validation (red on invalid blur, green when valid)
   - Cloudinary unsigned upload (drag-drop + click)
   - Image previews + remove
   - On submit: all data POSTed to Netlify function which creates
     a Shopify Customer + sends email to hello@skinnerandvale.com
   - Success state replaces form
   ============================================================ */
(function () {
  'use strict';

  const SUBMIT_ENDPOINT = 'https://skinner-and-vale-forms.netlify.app/.netlify/functions/bespoke-submit';

  const CLOUDINARY_CLOUD_NAME = 'dqjl7wkdt';
  const CLOUDINARY_UPLOAD_PRESET = 'jacket_customizer';
  const CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;
  const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10MB per file
  const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

  document.querySelectorAll('.bf-form').forEach(initForm);

  /* ---- Form init ---- */
  function initForm(form) {
    const submitBtn = form.querySelector('.bf-submit');
    const successEl = form.parentElement.querySelector('.bf-success');
    const uploader = form.querySelector('.bf-uploader');
    const fileInput = form.querySelector('.bf-uploader-input');
    const thumbs = form.querySelector('.bf-thumbs');
    const uploadedField = form.querySelector('input[name="contact[uploaded_images]"]');
    const uploadedUrls = [];

    // Validate every input
    form.querySelectorAll('.bf-input, .bf-textarea, .bf-select').forEach(field => {
      field.addEventListener('blur', () => validateField(field));
      field.addEventListener('input', () => {
        // Clear error styling as soon as they start typing
        if (field.classList.contains('is-error')) {
          field.classList.remove('is-error');
          field.closest('.bf-group')?.classList.remove('has-error');
        }
        updateSubmitState(form);
      });
    });

    // Validate radio/checkbox groups (have data-required-group on the wrapper)
    form.querySelectorAll('[data-required-group]').forEach(group => {
      group.querySelectorAll('input').forEach(input => {
        input.addEventListener('change', () => {
          validateGroup(group);
          updateSubmitState(form);
        });
      });
    });

    // File uploader
    if (uploader && fileInput) {
      uploader.addEventListener('click', e => {
        if (e.target.closest('.bf-thumb-remove')) return;
        fileInput.click();
      });
      uploader.addEventListener('dragover', e => {
        e.preventDefault();
        uploader.classList.add('is-drag');
      });
      uploader.addEventListener('dragleave', () => uploader.classList.remove('is-drag'));
      uploader.addEventListener('drop', e => {
        e.preventDefault();
        uploader.classList.remove('is-drag');
        handleFiles(e.dataTransfer.files);
      });
      fileInput.addEventListener('change', e => handleFiles(e.target.files));
    }

    function handleFiles(fileList) {
      Array.from(fileList).forEach(file => {
        if (!ACCEPTED_TYPES.includes(file.type)) {
          alert(`"${file.name}" is not a supported image format. Please upload JPG, PNG, WEBP or HEIC.`);
          return;
        }
        if (file.size > MAX_FILE_BYTES) {
          alert(`"${file.name}" is over 10MB. Please use a smaller file.`);
          return;
        }
        uploadFile(file);
      });
      fileInput.value = ''; // allow re-selecting same file
    }

    function uploadFile(file) {
      // Build thumbnail with progress
      const thumb = document.createElement('div');
      thumb.className = 'bf-thumb';
      const img = document.createElement('img');
      img.alt = file.name;
      const reader = new FileReader();
      reader.onload = e => { img.src = e.target.result; };
      reader.readAsDataURL(file);

      const progress = document.createElement('div');
      progress.className = 'bf-thumb-progress';
      progress.textContent = '0%';

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'bf-thumb-remove';
      removeBtn.setAttribute('aria-label', 'Remove image');
      removeBtn.innerHTML = '&#10005;';

      thumb.appendChild(img);
      thumb.appendChild(progress);
      thumb.appendChild(removeBtn);
      thumbs.appendChild(thumb);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('folder', 'bespoke-forms');

      const xhr = new XMLHttpRequest();
      xhr.open('POST', CLOUDINARY_UPLOAD_URL);
      xhr.upload.onprogress = e => {
        if (e.lengthComputable) {
          progress.textContent = Math.round((e.loaded / e.total) * 100) + '%';
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const res = JSON.parse(xhr.responseText);
            const url = res.secure_url;
            if (url) {
              progress.remove();
              thumb.dataset.url = url;
              uploadedUrls.push(url);
              syncUploadedField();
              removeBtn.addEventListener('click', () => {
                const i = uploadedUrls.indexOf(url);
                if (i > -1) uploadedUrls.splice(i, 1);
                thumb.remove();
                syncUploadedField();
              });
              return;
            }
          } catch (e) { /* fall through to error */ }
        }
        progress.textContent = 'Failed';
        progress.style.color = '#d22e2e';
      };
      xhr.onerror = () => {
        progress.textContent = 'Failed';
        progress.style.color = '#d22e2e';
      };
      xhr.send(formData);
    }

    function syncUploadedField() {
      if (uploadedField) {
        uploadedField.value = uploadedUrls.length
          ? uploadedUrls.map((u, i) => `${i + 1}. ${u}`).join('\n')
          : '';
      }
    }

    // Disable browser native validation — our JS handles everything
    form.setAttribute('novalidate', '');
    submitBtn.disabled = false;

    // Submit handler — validates all, then POSTs to Netlify function
    form.addEventListener('submit', e => {
      e.preventDefault();

      const allValid = validateAll(form);
      if (!allValid) {
        const firstError = form.querySelector('.bf-group.has-error, .bf-confirmations.has-error');
        if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      // Gather all form fields into a flat object (arrays for multi-value)
      const payload = {};
      new FormData(form).forEach((value, key) => {
        if (Object.prototype.hasOwnProperty.call(payload, key)) {
          payload[key] = [].concat(payload[key], value);
        } else {
          payload[key] = value;
        }
      });

      submitBtn.disabled = true;
      submitBtn.textContent = 'Sending…';

      fetch(SUBMIT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(r => r.json())
        .then(() => {
          // Show success state regardless of partial warnings
          form.style.display = 'none';
          if (successEl) {
            successEl.classList.add('is-visible');
            successEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        })
        .catch(() => {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Submit';
          alert('Something went wrong — please try again or email us directly at hello@skinnerandvale.com');
        });
    });
  }

  /* ---- Validation ---- */
  function validateField(field) {
    const group = field.closest('.bf-group');
    const isRequired = field.hasAttribute('required');
    const value = (field.value || '').trim();
    let valid = true;
    let msg = '';

    if (isRequired && !value) {
      valid = false;
      msg = 'This field is required.';
    } else if (field.type === 'email' && value) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        valid = false;
        msg = 'Please enter a valid email.';
      }
    } else if (field.type === 'tel' && value) {
      if (!/^[0-9 +()-]{6,}$/.test(value)) {
        valid = false;
        msg = 'Please enter a valid phone number.';
      }
    }

    field.classList.toggle('is-error', !valid);
    field.classList.toggle('is-valid', valid && value !== '');
    if (group) {
      group.classList.toggle('has-error', !valid);
      const errEl = group.querySelector('.bf-error-msg');
      if (errEl && msg) errEl.textContent = msg;
    }
    return valid;
  }

  function validateGroup(group) {
    const inputs = group.querySelectorAll('input');
    const checked = Array.from(inputs).some(i => i.checked);
    group.classList.toggle('has-error', !checked);
    return checked;
  }

  function validateAll(form) {
    let allValid = true;

    // Text / email / tel / select / textarea
    form.querySelectorAll('.bf-input[required], .bf-textarea[required], .bf-select[required]').forEach(f => {
      if (!validateField(f)) allValid = false;
    });

    // Radio groups
    form.querySelectorAll('[data-required-group]').forEach(g => {
      if (!validateGroup(g)) allValid = false;
    });

    // Required confirmation checkboxes (.bf-confirmations)
    form.querySelectorAll('.bf-confirmations, .bf-confirm-inline').forEach(box => {
      const required = box.querySelectorAll('input[type="checkbox"][required]');
      const allTicked = Array.from(required).every(c => c.checked);
      box.classList.toggle('has-error', required.length > 0 && !allTicked);
      if (required.length > 0 && !allTicked) allValid = false;
    });

    return allValid;
  }

  function updateSubmitState(form) {
    const submitBtn = form.querySelector('.bf-submit');
    if (!submitBtn) return;
    let allValid = true;
    form.querySelectorAll('.bf-input[required], .bf-textarea[required], .bf-select[required]').forEach(f => {
      const val = (f.value || '').trim();
      if (!val) allValid = false;
      else if (f.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) allValid = false;
      else if (f.type === 'tel' && !/^[0-9 +()-]{6,}$/.test(val)) allValid = false;
    });
    form.querySelectorAll('[data-required-group]').forEach(g => {
      const checked = Array.from(g.querySelectorAll('input')).some(i => i.checked);
      if (!checked) allValid = false;
    });
    form.querySelectorAll('input[type="checkbox"][required]').forEach(c => {
      if (!c.checked) allValid = false;
    });
    submitBtn.disabled = !allValid;
  }

  // Re-check submit gating when checkboxes toggle
  document.addEventListener('change', e => {
    if (e.target.matches('input[type="checkbox"], input[type="radio"]')) {
      const form = e.target.closest('.bf-form');
      if (form) updateSubmitState(form);
    }
  });

  // Reveal animation handled by theme.js IntersectionObserver (.reveal → is-revealed)
})();
